import { ApolloClient } from '@apollo/client';
import { evolve, pathEq, reject } from 'ramda';

import { TransactionType } from '__generated__/GQL';
import mergeConnectionResultsBy from 'common/graphql/mergeConnectionResultsBy';
import mergeBy from 'common/mergeBy';
import logger from 'helpers/logger';

import { OnUpdatedTransactionSubscription } from './graphql/subscriptions/onUpdatedTransaction.gql';
import {
  TransactionsSearchDocument,
  TransactionsSearchQueryVariables,
} from './hooks/useTransactionsSearch/transactionsSearch.gql';
import {
  TransactionSearchEdge,
  TransactionsFiltersFormToQueryVariableArgs,
} from './types';

const TRANSACTIONS_FIRST = 50;

export const removeTransactionFromCache = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  store: ApolloClient<any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query: any,
  transactionId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  variables: any,
): void => {
  try {
    const cache = store.readQuery({
      query,
      variables,
    });
    store.writeQuery({
      data: evolve(
        {
          viewer: {
            transactions: {
              edges: reject(pathEq(transactionId, ['node', 'transactionId'])),
            },
          },
        },
        cache,
      ),
      query,
      variables,
    });
  } catch (error) {
    logger.log(
      'removeTransactionFromCache failed: there was no cache for this query',
    );
  }
};

export const mergeTransactionConnectionResults =
  mergeConnectionResultsBy('transactionId');

const transactionEdgeEquals = (
  a: TransactionSearchEdge,
  b: TransactionSearchEdge,
) => {
  return a.node.transactionId === b.node.transactionId;
};

export type OnUpdatedTransactionSubscriptionType =
  OnUpdatedTransactionSubscription['updatedTransaction'];

export const addTransactionToCache = ({
  query,
  store,
  transaction,
}: {
  store: ApolloClient<unknown>;
  query: typeof TransactionsSearchDocument;
  transaction: OnUpdatedTransactionSubscriptionType;
}) => {
  try {
    const transactionSearchEdge: TransactionSearchEdge = {
      __typename: 'TransactionSearchEdge',
      node: {
        ...transaction,
        __typename: 'Transaction',
      },
    };

    const cache = store.readQuery({
      query,
    });

    if (!cache) {
      throw new Error('No cache found');
    }

    const newData = {
      ...cache,
      viewer: {
        ...cache.viewer,
        company: {
          ...cache.viewer.company,
          transactionsSearch: {
            ...cache.viewer.company.transactionsSearch,
            edges: mergeBy(
              transactionEdgeEquals,
              cache.viewer.company.transactionsSearch.edges,
              [transactionSearchEdge],
            ),
          },
        },
      },
    };

    store.writeQuery({
      data: newData,
      query,
    });
  } catch (error) {
    logger.log(
      'addTransactionToCache failed: there was no cache for this query',
    );
  }
};

const hasValue = <T>(value: T) =>
  Array.isArray(value) ? value.length : Boolean(value);

export const getFiltersLength = <T extends Record<string, unknown>>(
  filters: T,
  ignoredKeys: Array<keyof T> = [],
) =>
  Object.keys(filters).reduce((filtersLength, curr) => {
    if (!ignoredKeys.includes(curr) && hasValue(filters[curr])) {
      return filtersLength + 1;
    }

    return filtersLength;
  }, 0);

export const mapTransactionsFiltersFormToQueryVariables = ({
  bankAccountId,
  companyProfileId,
  filters: {
    afterDate,
    beforeDate,
    companyUserId,
    payin,
    paymentMethod,
    payout,
    q,
    status,
    valueGreaterThan,
    valueLesserThan,
    withoutReceipts,
  },
}: TransactionsFiltersFormToQueryVariableArgs): TransactionsSearchQueryVariables => ({
  after: null,
  companyProfileId,
  filters: {
    afterDate,
    bankAccountId,
    beforeDate,
    companyProfileId: bankAccountId ? undefined : companyProfileId,
    companyUserId: companyUserId ? [companyUserId] : undefined,
    hasReceipts: withoutReceipts ? false : undefined,
    isPersonal: withoutReceipts ? false : undefined,
    paymentMethod: paymentMethod.length ? paymentMethod : undefined,
    searchTerm: q,
    status: status.length ? status : undefined,
    type:
      payin || payout
        ? [
            ...(payin ? [TransactionType.Payin] : []),
            ...(payout ? [TransactionType.Payout] : []),
          ]
        : undefined,
    valueGreaterThan: valueGreaterThan ? +valueGreaterThan : undefined,
    valueLesserThan: valueLesserThan ? +valueLesserThan : undefined,
  },
  first: TRANSACTIONS_FIRST,
});
